// src/services/income.service.ts
// Manual income (non-invoice revenue). Provider-only — the `incomeRecords`
// collection is locked to provider roles in firestore.rules.
import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Income, CreateIncomeDto, UpdateIncomeDto } from '../types/finance.types';

const COLLECTION_NAME = 'incomeRecords';
const generateId = () => `inc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const stripUndefined = (o: Record<string, any>) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

export async function createIncome(data: CreateIncomeDto, userId: string): Promise<Income> {
  const id = generateId();
  const income: Income = {
    id,
    date: Timestamp.fromDate(data.date),
    amount: data.amount,
    category: data.category,
    description: data.description,
    status: data.status,
    paymentMethod: data.paymentMethod,
    projectId: data.projectId,
    projectName: data.projectName,
    organizationId: data.organizationId,
    clientName: data.clientName,
    notes: data.notes,
    createdAt: Timestamp.now(),
    createdBy: userId,
    updatedAt: Timestamp.now(),
  };
  await setDoc(doc(db, COLLECTION_NAME, id), stripUndefined(income));
  return income;
}

export async function getIncome(filters?: {
  startDate?: Date;
  endDate?: Date;
  projectId?: string;
}): Promise<Income[]> {
  let q = query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'));
  if (filters?.projectId) q = query(q, where('projectId', '==', filters.projectId));
  const snapshot = await getDocs(q);
  let records = snapshot.docs.map((d) => d.data() as Income);
  if (filters?.startDate) records = records.filter((r) => r.date.toDate() >= filters.startDate!);
  if (filters?.endDate) records = records.filter((r) => r.date.toDate() <= filters.endDate!);
  return records;
}

export async function getIncomeById(id: string): Promise<Income | null> {
  const snap = await getDoc(doc(db, COLLECTION_NAME, id));
  return snap.exists() ? (snap.data() as Income) : null;
}

export async function updateIncome(id: string, data: UpdateIncomeDto): Promise<void> {
  const updateData: any = { updatedAt: Timestamp.now() };
  if (data.date !== undefined) updateData.date = Timestamp.fromDate(data.date);
  if (data.amount !== undefined) updateData.amount = data.amount;
  if (data.category !== undefined) updateData.category = data.category;
  if (data.description !== undefined) updateData.description = data.description;
  if (data.status !== undefined) updateData.status = data.status;
  if (data.paymentMethod !== undefined) updateData.paymentMethod = data.paymentMethod;
  if (data.projectId !== undefined) updateData.projectId = data.projectId;
  if (data.projectName !== undefined) updateData.projectName = data.projectName;
  if (data.organizationId !== undefined) updateData.organizationId = data.organizationId;
  if (data.clientName !== undefined) updateData.clientName = data.clientName;
  if (data.notes !== undefined) updateData.notes = data.notes;
  await updateDoc(doc(db, COLLECTION_NAME, id), updateData);
}

export async function deleteIncome(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION_NAME, id));
}

// Total received income in a date range (drives revenue + margin).
export async function getReceivedIncomeTotal(filters?: { startDate?: Date; endDate?: Date }): Promise<number> {
  const records = await getIncome(filters);
  return records.filter((r) => r.status === 'received').reduce((sum, r) => sum + (r.amount || 0), 0);
}

export const incomeService = {
  createIncome,
  getIncome,
  getIncomeById,
  updateIncome,
  deleteIncome,
  getReceivedIncomeTotal,
};
